// App shell: real session gate, routing, fail-closed flag status, toasts. No demo state.
import React from "react";
import { Banner, Button, Card } from "@/ds";
import { cockpit, withTenantContext } from "@/api";
import type { HostEnvironmentInfo, RequestListItem } from "@/api";
import { Icon } from "@/lib";
import { roleAtLeast, roleLabelKey } from "@/lib/roles";
import { sessionIdentity } from "@/lib/session-identity";
import { useSession } from "./session";
import { useLiveInvalidation, useLiveInvalidationSinceReady, useLiveReady, useLiveScopeKey } from "./live/live-provider";
import { useT } from "./i18n";
import { PRODUCT_NAME_FULL } from "../../product-name";
import { Wordmark } from "./brand";
import { NAVIGATION_SETTLE_MS, SIGN_IN_SETTLE_MS, usePageSettle } from "./page-settle";
import {
  useRouter,
  allowedWhenSignedOut,
  pathOf,
  viewFromPath,
  type SettingsSection,
  type View,
  type WorklistReturnEntry,
} from "./router";
import { SidebarShell, type ShellNavEntry, type ShellNavItem } from "./shell-sidebar";
import { shellNavKeys, SHELL_GROUPS, type ShellNavKey } from "./shell-nav";
import {
  activeSuppressionIds,
  buildAssignmentSnapshot,
  newlyAssignedToOperator,
  ownPrincipalKeys,
  type AssignmentSnapshot,
} from "./assignment-notifications";
import {
  buildArrivalSnapshot,
  detectInboundArrivals,
  mergeArrivalNotice,
  type ArrivalNotice,
  type ArrivalSnapshot,
} from "./notifications/inbound-arrivals";
import {
  installNotificationAudioUnlock,
  NotificationAudioEngine,
} from "./notifications/notification-audio";
import {
  readNotificationSoundsEnabled,
  writeNotificationSoundsEnabled,
} from "./notifications/notification-sound-preference";
import { NotificationProvider, useNotifications } from "./notifications/notification-host";
import { InvitationScreen } from "./screens/screen-invitation";
import { StaffInvitationScreen } from "./screens/screen-staff-invitation";
import { LoginScreen } from "./screens/screen-login";
import { PasswordForgotScreen, PasswordResetScreen } from "./screens/screen-password-reset";
import { DeniedScreen } from "./screens/screen-denied";
import { AccountScreen } from "./screens/screen-account";
import { CatalogScreen } from "./screens/screen-catalog";
import { InboxScreen } from "./screens/screen-inbox";
import { FiledScreen } from "./screens/screen-filed";
import type { OverviewHeadList } from "./screens/overview-head-tools";
import { WorkspaceScreen } from "./screens/screen-workspace";
import { GettingStartedScreen } from "./screens/screen-getting-started";
import { StaffScreen } from "./screens/screen-staff";
import { BusinessRuleValuesEditor } from "./screens/screen-business-rule-values";
import { SettingsScreen } from "./screens/screen-settings";
import {
  canAccessRuleReference,
  RuleReferenceScreen,
} from "./screens/screen-rule-reference";
import { TenantsScreen } from "./screens/screen-tenants";
import { canAccessBusinesses } from "./screens/screen-tenants-access";
import { SettingsEmailSection } from "./screens/settings-email";
import { PlatformMailboxesScreen } from "./screens/screen-platform-mailboxes";
import { AgentRulesLabScreen } from "./screens/reqventos-agent-rules-lab/screen";
import { EmailQaEntry } from "./screens/screen-email-qa-entry";
import { testMailContextKey } from "./screens/test-mail-access";
import {
  writePrimaryScrollTop,
  withoutTrashedRequest,
  type InboxReturnContext,
  type WorklistReturnContext,
} from "./worklist-return-context";

const LOCAL_ASSIGNMENT_SUPPRESSION_MS = 20_000;
/** All inbound mail shares one toast, so a burst aggregates instead of stacking (#536). */
const ARRIVAL_TOAST_KEY = "inbound-arrivals";

/** What the tab is called, and what the main region announces. */
function routeTitleKey(view: View): string {
  switch (view.name) {
    case "inbox": return "overview.workTitle";
    case "filed": return "filed.title";
    case "mail": return "ws.review.customerRequest";
    case "account": return "account.title";
    case "staff": return "staff.title";
    case "settings": return "settings.title";
    case "ruleReference": return "ruleReference.shellTitle";
    case "businesses": return "businesses.title";
    case "emailQa": return "emailQa.title";
    case "agentRulesLab": return "agentLab.title";
    case "catalog": return "catalog.title";
    case "invitation": return "invite.accept.title";
    case "staffInvitation": return "staffInvite.accept.routeTitle";
    case "passwordForgot": return "login.forgot.title";
    case "passwordReset": return "login.reset.title";
    case "login": return "login.heading";
  }
}

const scrollMainTop = () => {
  writePrimaryScrollTop(0);
  // Reassert after the new route paints so a deliberate destination or rejected cross-tenant
  // entry starts at the top of the newly mounted layout.
  if (typeof window !== "undefined") {
    window.requestAnimationFrame(() => writePrimaryScrollTop(0));
  }
};

interface StoredWorklistContext {
  scopeKey: string;
  value: WorklistReturnContext;
}

interface PendingTenantNavigation {
  entryKey: string;
  destination: View;
  preserveSource?: boolean;
}

export function App() {
  return <NotificationProvider><HostEnvironmentBadge /><AppShell /></NotificationProvider>;
}

/**
 * #2350: a dev host says so on every screen, sign-in included; production renders nothing. Fixed
 * and click-through, so it changes no layout and hides no control. A failed read shows nothing:
 * the badge only informs, and the live system must never show it by mistake.
 */
function HostEnvironmentBadge() {
  const t = useT();
  const [environment, setEnvironment] = React.useState<HostEnvironmentInfo["environment"] | null>(null);
  React.useEffect(() => {
    let live = true;
    cockpit.getHostEnvironment().then(
      (info) => { if (live) setEnvironment(info.environment); },
      () => undefined,
    );
    return () => { live = false; };
  }, []);
  if (environment !== "dev") return null;
  // At phone width the full sentence would cover the logo and the account control; the short
  // form fits the gap between them, and the label keeps the whole sentence for screen readers.
  return (
    <div className="host-environment-badge" role="note" aria-label={t("shell.devEnvironment")}>
      <span className="host-environment-badge__long" aria-hidden="true">{t("shell.devEnvironment")}</span>
      <span className="host-environment-badge__short" aria-hidden="true">{t("shell.devEnvironmentShort")}</span>
    </div>
  );
}

function AppShell() {
  const { user, ready, config, logout, refreshUser, refreshConfig, tenantContext, switchTenant } = useSession();
  const t = useT();
  const scopeKey = useLiveScopeKey();
  // #2266: a page's first load waits for the live stream, so its opening event loads nothing again.
  const liveReady = useLiveReady();
  const { view, entry, navigate, returnToWorklist } = useRouter();
  const mainRef = React.useRef<HTMLElement>(null);
  const previousRouteEntryKey = React.useRef(entry.key);
  const authenticated = ready && user !== null;
  const routeTitle = t(routeTitleKey(view));
  const notifications = useNotifications();
  const pushToast = notifications.push;
  // #1543: a page opened in the console appears once, with its data. After sign-in or a reload the
  // loading screen stays up over the shell until the first page and the configuration are in.
  const [signedInPageShown, setSignedInPageShown] = React.useState(false);
  const markPageShown = React.useCallback(() => setSignedInPageShown(true), []);
  usePageSettle(mainRef, authenticated ? `${scopeKey}|${entry.key}` : null, {
    limitMs: signedInPageShown ? NAVIGATION_SETTLE_MS : SIGN_IN_SETTLE_MS,
    waiting: (!signedInPageShown && config === null) || !liveReady,
    onShown: markPageShown,
  });
  React.useEffect(() => {
    if (!authenticated) setSignedInPageShown(false);
  }, [authenticated]);
  // #2185: the top-nav badge — how many requests nobody on the team has opened yet, the number
  // of the start page's "New" card. It was the number of requests to process (#376, #2003).
  const [unopenedCount, setUnopenedCount] = React.useState<number | null>(null);
  // #794: how much arrived at the published address and was set aside as not an enquiry.
  const [filedCount, setFiledCount] = React.useState(0);
  // #2175: Archive and Trash are states of the start page, not routes. Selecting the start page
  // while it is open changes no route, so this count remounts the screen on its default list.
  const [inboxHomeCount, setInboxHomeCount] = React.useState(0);
  // #2259: the list a head switch on Filed asked the start page to open. Archive and Trash are
  // states of the start page rather than addresses (#2175), so the list rides along with one
  // navigation and is dropped once the start page has opened on it.
  const [requestedInboxList, setRequestedInboxList] = React.useState<OverviewHeadList | null>(null);
  React.useEffect(() => {
    if (view.name === "inbox") setRequestedInboxList(null);
  }, [view.name, entry.key]);
  const assignmentSnapshot = React.useRef<AssignmentSnapshot | null>(null);
  // #803: work can land on the operator through a team, so "is this mine" needs the shifts they
  // are on. Ids only, and only their own — the full team list stays behind the admin rank.
  const ownTeamIds = React.useRef<readonly string[]>([]);
  const localAssignmentSuppressions = React.useRef<Map<string, number>>(new Map());
  const assignmentSequence = React.useRef(0);
  const scopeKeyRef = React.useRef(scopeKey);
  const worklistContexts = React.useRef<Map<string, StoredWorklistContext>>(new Map());
  const previousContextScope = React.useRef(scopeKey);
  const pendingTenantNavigation = React.useRef<PendingTenantNavigation | null>(null);
  const arrivalSnapshot = React.useRef<ArrivalSnapshot | null>(null);
  const arrivalNotice = React.useRef<Record<"ready" | "attention", ArrivalNotice | null>>({ ready: null, attention: null });
  const notificationAudio = React.useRef<NotificationAudioEngine | null>(null);
  if (!notificationAudio.current) notificationAudio.current = new NotificationAudioEngine();
  const [soundPreference, setSoundPreference] = React.useState<{
    userId: string;
    enabled: boolean;
  } | null>(null);
  const notificationSoundsEnabled = user
    ? soundPreference?.userId === user.id
      ? soundPreference.enabled
      : readNotificationSoundsEnabled(user.id)
    : true;
  const notificationSoundsEnabledRef = React.useRef(notificationSoundsEnabled);
  notificationSoundsEnabledRef.current = notificationSoundsEnabled;
  React.useEffect(() => {
    document.title = `${routeTitle} — ${PRODUCT_NAME_FULL}`;
  }, [routeTitle]);

  React.useEffect(() => {
    if (previousRouteEntryKey.current === entry.key) return;
    previousRouteEntryKey.current = entry.key;
    if (!authenticated) return;
    const frame = window.requestAnimationFrame(() => {
      mainRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [authenticated, entry.key]);

  // The open request is never announced (#536). A ref, not a dependency: rebuilding the refresh
  // callback on every navigation would refetch the worklist each time an operator opens a mail.
  const openRequestRoute = React.useRef<string | null>(null);
  openRequestRoute.current = view.name === "mail" ? view.id : null;

  if (previousContextScope.current !== scopeKey) {
    // The render itself cannot read old-tenant state. The effect below also invalidates history
    // entries so browser Back cannot expose a prior request reference under the new tenant.
    worklistContexts.current.clear();
    previousContextScope.current = scopeKey;
  }

  const rememberInboxContext = React.useCallback((value: InboxReturnContext) => {
    worklistContexts.current.set(entry.key, { scopeKey, value });
  }, [entry.key, scopeKey]);

  React.useEffect(() => {
    const engine = notificationAudio.current!;
    void engine.prepare();
    return installNotificationAudioUnlock(engine);
  }, []);

  const changeNotificationSoundsEnabled = React.useCallback((enabled: boolean) => {
    if (!user) return;
    notificationSoundsEnabledRef.current = enabled;
    writeNotificationSoundsEnabled(user.id, enabled);
    setSoundPreference({ userId: user.id, enabled });
  }, [user]);

  const suppressAssignmentNotice = React.useCallback((requestId: string) => {
    localAssignmentSuppressions.current.set(requestId, Date.now() + LOCAL_ASSIGNMENT_SUPPRESSION_MS);
  }, []);

  const isOwner = !!user && canAccessBusinesses(user.role);

  // #1283: the Businesses screen is the only place a session enters a business. Nothing in the
  // header selects one any more, so the shell no longer keeps a list of them.
  const browseTenant = React.useCallback(async (tenantId: string) => {
    worklistContexts.current.clear();
    pendingTenantNavigation.current = {
      entryKey: entry.key,
      destination: { name: "inbox" },
      preserveSource: true,
    };
    await switchTenant(tenantId);
  }, [entry.key, switchTenant]);

  const openReq = React.useCallback((id: string) => {
    let returnTo: WorklistReturnEntry | null = null;
    if (view.name === "inbox" || view.name === "emailQa") {
      returnTo = {
        key: entry.key,
        index: entry.index,
        view: view.name,
        scopeKey,
      };
    } else if (view.name === "mail" && entry.returnTo?.scopeKey === scopeKey) {
      returnTo = entry.returnTo;
    }
    navigate({ name: "mail", id }, { scopeKey, returnTo });
    scrollMainTop();
  }, [entry.index, entry.key, entry.returnTo, navigate, scopeKey, view.name]);
  // #2302: the arrival toast opens a request through this ref. As a dependency, `openReq` rebuilt
  // the refresh below on every navigation, and each rebuild re-read the whole worklist.
  const openReqRef = React.useRef(openReq);
  openReqRef.current = openReq;

  // Mail that arrived while the operator was working elsewhere in the console (#536). One toast
  // for the whole burst: it opens the request when a single one is meant, else the start page.
  const announceArrivals = React.useCallback((rows: RequestListItem[]) => {
    const previous = arrivalSnapshot.current;
    if (!previous) return;
    const arrivals = detectInboundArrivals(previous, rows, openRequestRoute.current);
    // Audio is one event per newly ready/attention work item, including an open request.
    // It is prepared ahead of time, so a visible toast and its sound start in the same turn.
    if (notificationSoundsEnabledRef.current) {
      for (const arrival of arrivals) notificationAudio.current?.play(arrival.sound);
    }
    for (const category of ["ready", "attention"] as const) {
      const toastArrivals = arrivals.filter((arrival) => !arrival.toastSuppressed && arrival.category === category);
      const mergeKey = `${ARRIVAL_TOAST_KEY}:${category}`;
      const existing = arrivalNotice.current[category];
      // A queued newer inbound invalidates the old ready invitation immediately on refresh.
      if (existing?.notificationId && !rows.some(row => row.incoming_work?.notification?.id === existing.notificationId)) {
        for (const id of notifications.activeIds()) if (notifications.get(id)?.mergeKey === mergeKey) notifications.dismiss(id);
        arrivalNotice.current[category] = null;
      }
      // Aggregate only into a notice the operator can still see: once the toast is dismissed or
      // opened, the next mail starts counting from one instead of inheriting a stale total.
      const showing = notifications.hasMergeKey(mergeKey);
      const notice = mergeArrivalNotice(
        showing ? arrivalNotice.current[category] : null,
        toastArrivals,
        Date.now(),
      );
      if (!notice) continue;
      arrivalNotice.current[category] = notice;
      const target = notice.count === 1 ? notice.routeId : null;
      pushToast({
        tone: notice.category === "attention" ? "warning" : "info",
        mergeKey,
        title: notice.category === "attention" ? t("incoming.attentionNotice", { n: notice.count }) : target
          ? t("incoming.readyNotice")
          : t("incoming.readyNotices", { n: notice.count }),
        msg: target
          ? t("toast.arrivalBody", {
              subject: notice.subject || t("common.noSubject"),
              from: notice.from || t("inbox.unknownSender"),
            })
          : t(notice.category === "attention" ? "incoming.attentionNoticesBody" : "toast.arrivalsBody"),
        open: {
          label: target ? t("toast.openRequest") : t("toast.openInbox"),
          run: () => {
            if (target) openReqRef.current(target);
            else {
              navigate({ name: "inbox" }, { scopeKey, returnTo: null });
              scrollMainTop();
            }
          },
        },
      });
    }
  }, [navigate, notifications, pushToast, scopeKey, t]);

  const refreshAssignments = React.useCallback((notify: boolean) => {
    if (!user) return;
    const mine = ++assignmentSequence.current;
    // A switch to another business while this is in flight must not paint the previous one's
    // count, and must not use its rows as the baseline for "newly assigned" notices.
    const fetchedFor = scopeKey;
    const isCurrent = () => mine === assignmentSequence.current && fetchedFor === scopeKeyRef.current;

    // The badge projection and request notification snapshots have independent failure and
    // latency domains. Handle each completion directly: a stalled projection must not hold back
    // successful assignment or inbound-arrival notifications.
    void cockpit.listConversations({ group_by: "request", limit: 1 }).then((page) => {
      // Only the newest refresh may paint, and only under the scope it was fetched for.
      // A server that does not say how many are unopened leaves the badge unknown, not at zero.
      if (isCurrent()) setUnopenedCount(page.counts.unopened ?? null);
    }).catch(() => {
      if (isCurrent()) setUnopenedCount(null);
    });

    // Refreshed alongside the rows: a team the operator was added to between two polls has to be
    // "mine" by the time those rows are compared. A failure here degrades to direct assignments
    // only, which is the pre-#803 behaviour rather than a broken screen.
    void cockpit.listOwnTeamIds().then((own) => {
      if (isCurrent()) ownTeamIds.current = own.teamIds;
    }).catch(() => undefined);

    // Refreshed on the same signal as the inbox: filed mail is written by the pipeline, not by
    // anyone in this session, so nothing else would move the badge until a reload.
    void cockpit.listFiledMessages().then((page) => {
      if (isCurrent()) setFiledCount(page.unread);
    }).catch(() => undefined);

    void cockpit.listRequests().then((rows) => {
      if (!isCurrent()) return;
      const previous = assignmentSnapshot.current;
      if (notify && previous) {
        const notices = newlyAssignedToOperator(
          previous,
          rows,
          ownPrincipalKeys(user.id, ownTeamIds.current),
          activeSuppressionIds(localAssignmentSuppressions.current),
        );
        for (const notice of notices.slice(0, 3)) {
          pushToast({
            tone: "info",
            title: t("inbox.assignmentNoticeTitle"),
            msg: t("inbox.assignmentNoticeBody", {
              subject: notice.subject || t("common.noSubject"),
              from: notice.from || t("inbox.unknownSender"),
            }),
          });
        }
        if (notices.length > 3) {
          pushToast({
            tone: "info",
            title: t("inbox.assignmentNoticeTitle"),
            msg: t("inbox.assignmentNoticeMore", { n: notices.length - 3 }),
          });
        }
      }
      if (notify) announceArrivals(rows);
      assignmentSnapshot.current = buildAssignmentSnapshot(rows);
      arrivalSnapshot.current = buildArrivalSnapshot(rows, arrivalSnapshot.current);
    }).catch(() => undefined);
  }, [announceArrivals, pushToast, scopeKey, t, user]);

  // Everything below the shell is per scope: the badge count, the "newly assigned" baseline and
  // the local suppressions all belong to one business and are dropped when it changes.
  React.useEffect(() => {
    scopeKeyRef.current = scopeKey;
    worklistContexts.current.clear();
    assignmentSnapshot.current = null;
    arrivalSnapshot.current = null;
    arrivalNotice.current = { ready: null, attention: null };
    localAssignmentSuppressions.current.clear();
    assignmentSequence.current += 1;
    setUnopenedCount(null);
    setFiledCount(0);
  }, [scopeKey]);

  React.useEffect(() => {
    if (!user || !liveReady) return;
    void refreshAssignments(false);
  }, [liveReady, refreshAssignments, user]);

  // The inbox badge and the assignment notices follow the requests domain (#544). Their first load
  // is made once the stream is ready, so only moves after it count (#2266). A return to the front
  // reads nothing of its own: a stream that went quiet meanwhile is reopened instead (#2274).
  const requestsInvalidations = useLiveInvalidationSinceReady("requests", "workspace", "pipeline", "catalog", "settings", "tenants");
  React.useEffect(() => {
    if (!user || requestsInvalidations === 0) return;
    void refreshAssignments(true);
  }, [requestsInvalidations, refreshAssignments, user]);

  // The shell renders server facts too: the AI and read-only banners, the effective flags and the
  // feature gates all come from /config and the session identity. Without this they would sit on
  // login-time values until a reload — the very thing the removed refresh button used to fix.
  const configInvalidations = useLiveInvalidation("settings", "tenants");
  React.useEffect(() => {
    if (!user || configInvalidations === 0) return;
    void refreshConfig({ background: true }).catch(() => undefined);
    void refreshUser();
  }, [configInvalidations, refreshConfig, refreshUser, user]);

  // Role changes are staff events. Refresh the identity so an already-open privileged surface
  // drops its data and controls when access is revoked, without reloading configuration.
  const staffInvalidations = useLiveInvalidation("staff");
  React.useEffect(() => {
    if (!user || staffInvalidations === 0) return;
    void refreshUser();
  }, [staffInvalidations, refreshUser, user]);

  // Keep the URL in sync with the session: show /login when signed out (remembering where the
  // operator was headed), and restore that target once signed in.
  const loginReturn = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!ready) return;
    if (!user) {
      if (!allowedWhenSignedOut(view)) {
        loginReturn.current = pathOf(view);
        navigate({ name: "login" }, { replace: true, scopeKey: null, returnTo: null });
      }
    } else if (view.name === "login") {
      const back = loginReturn.current;
      loginReturn.current = null;
      const target = back && back !== pathOf({ name: "login" }) ? viewFromPath(back) : { name: "inbox" as const };
      navigate(target, { replace: true, scopeKey, returnTo: null });
    } else if (
      view.name === "invitation"
      || view.name === "staffInvitation"
      || view.name === "passwordForgot"
      || view.name === "passwordReset"
    ) {
      // Signed in already. The link is for somebody who has no account, and accepting it would
      // create a second one — so this goes to the product instead, and `replace` takes the token
      // out of the address bar on the way. A signed-in person changes their password under
      // Account, so the reset screens (#1478) follow the same rule.
      navigate({ name: "inbox" }, { replace: true, scopeKey, returnTo: null });
    }
  }, [ready, scopeKey, user, view, navigate]);

  // Every authenticated history entry is stamped with the tenant/session scope that created it.
  // The values themselves remain only in `worklistContexts`; a stale entry can therefore be
  // rejected before any old request id or filter is rendered under a newly selected business.
  // A business switch blanks the shown business name before its scope changes; reacting to that
  // identity object would drop the pending switch navigation, so only signing in re-runs this.
  const signedIn = Boolean(user);
  React.useEffect(() => {
    if (!ready || !signedIn) return;
    if (
      view.name === "login"
      || view.name === "invitation"
      || view.name === "staffInvitation"
      || view.name === "passwordForgot"
      || view.name === "passwordReset"
    ) return;
    if (entry.scopeKey === scopeKey) {
      pendingTenantNavigation.current = null;
      return;
    }

    const pending = pendingTenantNavigation.current;
    pendingTenantNavigation.current = null;
    if (entry.scopeKey === null && !pending) {
      navigate(view, {
        replace: true,
        preserveSearch: true,
        scopeKey,
        returnTo: entry.returnTo,
      });
      return;
    }

    const pendingMatches = pending?.entryKey === entry.key;
    if (pendingMatches && pending.preserveSource) {
      navigate(view, { replace: true, scopeKey, returnTo: null });
      navigate(pending.destination, { scopeKey, returnTo: null });
      scrollMainTop();
      return;
    }
    const destination = pendingMatches ? pending.destination : { name: "inbox" as const };
    navigate(destination, { replace: true, scopeKey, returnTo: null });
    scrollMainTop();
  }, [entry.key, entry.returnTo, entry.scopeKey, navigate, ready, scopeKey, signedIn, view]);

  const go = (r: "inbox" | "account" | "staff" | "settings" | "ruleReference" | "businesses" | "emailQa" | "catalog" | "filed" | "agentRulesLab") => {
    if (r === "inbox" && view.name === "inbox") {
      // The router keeps the history entry when the address does not change, so the list this
      // entry remembers would otherwise come straight back.
      worklistContexts.current.delete(entry.key);
      setInboxHomeCount((count) => count + 1);
    }
    navigate({ name: r }, { scopeKey, returnTo: null });
    scrollMainTop();
  };
  /**
   * One Settings section, from inside Settings (#1431). It replaces the entry, like the rail does,
   * so Back leaves Settings rather than stepping back through the sections visited.
   */
  const openSettingsSection = (section: SettingsSection) => {
    navigate({ name: "settings", section }, { replace: true, scopeKey });
    scrollMainTop();
  };
  const reconcileTrashedRequest = (requestId: string) => {
    for (const [key, stored] of worklistContexts.current) {
      if (stored.scopeKey !== scopeKey) continue;
      worklistContexts.current.set(key, { ...stored, value: {
        ...stored.value,
        countsKnown: false,
        conversationPage: withoutTrashedRequest(stored.value.conversationPage, requestId),
        sectionPages: Object.fromEntries(Object.entries(stored.value.sectionPages).map(([section, page]) =>
          [section, withoutTrashedRequest(page, requestId)],
        )) as InboxReturnContext["sectionPages"],
      } });
    }
  };
  const returnFromWorkspace = () => {
    if (!returnToWorklist(scopeKey)) go("inbox");
  };

  // ---- not yet resolved ----
  if (!ready) {
    return (
      <div className="app-root">
        <div className="center-screen session-loading" role="status" aria-busy="true">
          <div className="login__mark">
            <Wordmark large />
          </div>
          <p className="subtle" style={{ marginTop: "var(--space-4)" }}>{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // ---- not authenticated ----
  if (!user) {
    // #780: accepting an invitation is how somebody GETS an account, so it has to be reachable
    // without one. Checked before the login gate rather than inside it, because sending an
    // invited owner to a sign-in form they cannot use would end the onboarding right there.
    if (view.name === "invitation") {
      return (
        <div className="app-root">
          <main className="public-route-main" aria-label={routeTitle}>
            <InvitationScreen />
          </main>
        </div>
      );
    }
    // #1298: the same reasoning for a colleague being added to a business that already exists.
    if (view.name === "staffInvitation") {
      return (
        <div className="app-root">
          <main className="public-route-main" aria-label={routeTitle}>
            <StaffInvitationScreen />
          </main>
        </div>
      );
    }
    // #1478: somebody who cannot sign in asks for a reset link here, and spends it on the next one.
    if (view.name === "passwordForgot" || view.name === "passwordReset") {
      return (
        <div className="app-root">
          <main className="public-route-main" aria-label={routeTitle}>
            {view.name === "passwordForgot" ? <PasswordForgotScreen /> : <PasswordResetScreen />}
          </main>
        </div>
      );
    }
    return (
      <div className="app-root">
        <main className="public-route-main" aria-label={routeTitle}>
          <LoginScreen />
        </main>
      </div>
    );
  }

  if (entry.scopeKey !== null && entry.scopeKey !== scopeKey) {
    return (
      <div className="app-root">
        <div className="center-screen">
          <p className="subtle">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  const role = user.role;
  const identity = sessionIdentity(user);
  // Rank-based (#230): the owner inherits every admin surface.
  const isAdmin = roleAtLeast(role, "admin");
  const testMailEnabled = testMailContextKey({ user, config, tenantContext }) !== null;
  const flags = config?.flags;
  const aiOff = flags ? !flags.ai_enabled : false;
  // Non-active tenant = the API rejects every mutation (#231); tell the operator why.
  const tenantReadOnly =
    user.tenantStatus === "suspended" || user.tenantStatus === "archived" ? user.tenantStatus : null;

  // The tenant-less owner has no tenant-scoped data to show (the API answers 403 without an
  // explicit context, #231) — every tenant-scoped view prompts for a business instead.
  const ownerNeedsContext = isOwner && !user.tenantId;
  // #1989: the header carries the logo of the business this session acts in. The configuration
  // is that business's own — blank while an owner switches, and without a logo for an owner who
  // has entered none — so the header and Settings → Logo can never disagree.
  const configuredLogo = config?.business_logo ?? null;
  const businessLogo = configuredLogo
    ? { url: withTenantContext(configuredLogo.url), width: configuredLogo.width, height: configuredLogo.height }
    : null;

  const visibleKeys = shellNavKeys({
    isAdmin, isOwner, inBusiness: !ownerNeedsContext,
    canReadRules: canAccessRuleReference(role),
    ruleValuesAvailable: Boolean(user.tenantId), testMailEnabled,
  });
  const requestedSection = view.name === "settings" ? view.section : undefined;
  const settingsSection = requestedSection && visibleKeys.includes(requestedSection)
    ? requestedSection : ownerNeedsContext ? "service-mailboxes" : "system";
  const settingEntry = (section: SettingsSection, label: string, icon: string): ShellNavEntry => ({
    key: section, label: t(label), icon, href: pathOf({ name: "settings", section }),
    active: view.name === "settings" && settingsSection === section,
    onClick: () => {
      navigate({ name: "settings", section }, { scopeKey, returnTo: null });
      scrollMainTop();
    },
  });
  const navEntries: Record<ShellNavKey, ShellNavEntry> = {
    // #2003: the start page. Filed is reached from its head and a request is opened from it, so
    // the entry stays lit on both; the badge is the number of unopened requests (#2185).
    inbox: {
      key: "inbox", icon: "inbox", href: pathOf({ name: "inbox" }), label: t("shell.overview"),
      badge: unopenedCount === null ? "—" : unopenedCount > 0 ? String(unopenedCount) : null,
      active: view.name === "inbox" || view.name === "mail" || view.name === "filed", onClick: () => go("inbox"),
    },
    staff: {
      key: "staff", icon: "users", href: pathOf({ name: "staff" }), label: t("shell.staff"),
      active: view.name === "staff", onClick: () => go("staff"),
    },
    setup: settingEntry("setup", "shell.gettingStarted", "flag"),
    email: settingEntry("email", "settings.section.email", "mail"),
    signature: settingEntry("signature", "settings.section.signature", "pencil"),
    logo: settingEntry("logo", "settings.section.logo", "grid"),
    "rule-values": settingEntry("rule-values", "settings.section.ruleValues", "scale"),
    system: settingEntry("system", "settings.section.system", "gauge"),
    "service-mailboxes": settingEntry("service-mailboxes", "shell.platformMailboxes", "server"),
    agentRulesLab: {
      key: "agentRulesLab", icon: "sparkles", href: pathOf({ name: "agentRulesLab" }),
      label: t("agentLab.title"), active: view.name === "agentRulesLab", onClick: () => go("agentRulesLab"),
    },
    ruleReference: {
      key: "ruleReference", icon: "fileText", href: pathOf({ name: "ruleReference" }), label: t("ruleReference.shellTitle"),
      active: view.name === "ruleReference", onClick: () => go("ruleReference"),
    },
    catalog: {
      key: "catalog", icon: "package", href: pathOf({ name: "catalog" }), label: t("shell.catalog"),
      active: view.name === "catalog", onClick: () => go("catalog"),
    },
    businesses: {
      key: "businesses", icon: "building", href: pathOf({ name: "businesses" }), label: t("shell.businesses"),
      active: view.name === "businesses", onClick: () => go("businesses"),
    },
    emailQa: {
      key: "emailQa", icon: "mail", href: pathOf({ name: "emailQa", section: isOwner ? undefined : "test-mail" }),
      label: t("emailQa.title"), active: view.name === "emailQa", onClick: () => {
        navigate({ name: "emailQa", section: isOwner ? undefined : "test-mail" }, { scopeKey, returnTo: null });
        scrollMainTop();
      },
    },
  };
  const group = (key: typeof SHELL_GROUPS[number]["key"]): ShellNavItem[] => {
    const definition = SHELL_GROUPS.find((item) => item.key === key)!;
    const entries = definition.entries.filter((entry) => visibleKeys.includes(entry)).map((entry) => navEntries[entry]);
    return entries.length ? [{ key, icon: definition.icon, label: t(`sidebar.group.${key}`), entries }] : [];
  };
  const nav: ShellNavItem[] = [
    ...(visibleKeys.includes("inbox") ? [navEntries.inbox] : []),
    ...group("masterData"), ...group("settings"),
    ...(visibleKeys.includes("ruleReference") ? [navEntries.ruleReference] : []),
    ...group("quality"), ...group("platform"),
  ];

  const storedWorklistContext = worklistContexts.current.get(entry.key);
  const restoredInboxContext = storedWorklistContext?.scopeKey === scopeKey
    && storedWorklistContext.value.kind === "inbox"
    ? storedWorklistContext.value
    : undefined;

  let content: React.ReactNode;
  if (view.name === "businesses") {
    content = isOwner
      ? <TenantsScreen pushToast={pushToast} tenantContext={tenantContext}
          onBrowse={(id) => { void browseTenant(id); }}
          selectedId={view.tenantId ?? null}
          onSelect={(tenantId, options) => navigate(
            tenantId ? { name: "businesses", tenantId } : { name: "businesses" },
            { scopeKey, returnTo: null, replace: options?.correcting === true },
          )}
          section={view.section}
          onSectionChange={(section) => navigate(
            { name: "businesses", tenantId: view.tenantId, section },
            { replace: true, scopeKey },
          )} />
      : <DeniedScreen reason="owner" area={t("shell.businesses")} onHome={() => go("inbox")} />;
  } else if (view.name === "agentRulesLab") {
    content = isOwner
      ? <AgentRulesLabScreen key={user.id} actorId={user.id} />
      : <DeniedScreen reason="owner" area={t("agentLab.title")} onHome={() => go("inbox")} />;
  } else if (view.name === "emailQa") {
    content = <EmailQaEntry
        section={view.section}
        onSectionChange={(section) => navigate({ name: "emailQa", section }, { replace: true, scopeKey })}
        runId={view.runId ?? null}
        onRunChange={(runId) => navigate(
          runId ? { name: "emailQa", section: "runs", runId } : { name: "emailQa", section: "runs" },
          { replace: true, scopeKey },
        )}
        onOpenRequest={openReq} onHome={() => go("inbox")} pushToast={pushToast} />;
  } else if (view.name === "account") {
    content = <AccountScreen
      user={user}
      onUserChanged={refreshUser}
      pushToast={pushToast}
      notificationSoundsEnabled={notificationSoundsEnabled}
      onNotificationSoundsEnabledChange={changeNotificationSoundsEnabled}
    />;
  } else if (view.name === "ruleReference") {
    content = canAccessRuleReference(role)
      ? <RuleReferenceScreen
          runtimeRulesetVersion={config?.ruleset_version ?? null}
          // A tenant-less owner has no business whose values these would be.
          ruleParameters={config && !ownerNeedsContext ? {
            values: config.rule_parameter_values ?? null,
            source: config.rule_parameters?.source ?? null,
            version: config.rule_parameters?.parameter_set_version ?? null,
          } : null}
          noBusiness={ownerNeedsContext}
        />
      : <DeniedScreen reason="admin" area={t("ruleReference.shellTitle")} onHome={() => go("inbox")} />;
  } else if (view.name === "settings") {
    // #1431: before `ownerNeedsContext`, like Businesses: a tenant-less owner still has the
    // platform group. The business sections are simply not offered without a business, and each
    // section's screen is refused by its own server routes to anyone who reaches it by hand.
    content = isAdmin
      ? <SettingsScreen
          config={config}
          pushToast={pushToast}
          onConfigChanged={refreshConfig}
          canToggleMasterSend={isOwner}
          section={view.section}
          onSectionChange={(section) => navigate({ name: "settings", section }, { replace: true, scopeKey })}
          inBusiness={!ownerNeedsContext}
          setup={ownerNeedsContext
            ? undefined
            : <GettingStartedScreen onOpenSignature={() => openSettingsSection("signature")} onOpenStaff={() => go("staff")} />}
          email={ownerNeedsContext ? undefined : (
            <SettingsEmailSection
              way={view.way}
              onWayChange={(way) => navigate({ name: "settings", section: "email", way }, { replace: true, scopeKey })}
              // The owner edits mailboxes through the owner routes of the business they entered.
              ownerTenantId={isOwner ? tenantContext ?? user.tenantId ?? null : null}
              pushToast={pushToast}
              onOpenSignature={() => openSettingsSection("signature")}
            />
          )}
          ruleValues={!ownerNeedsContext && user.tenantId ? <BusinessRuleValuesEditor
            key={`${user.id}:${user.tenantId}`} tenantId={user.tenantId} pushToast={pushToast}
            enabled={config?.features?.rule_values_edit === true} /> : undefined}
          serviceMailboxes={isOwner ? <PlatformMailboxesScreen pushToast={pushToast} /> : undefined}
          onOpenBusinesses={isOwner ? () => go("businesses") : undefined}
        />
      : <DeniedScreen reason="admin" area={t("shell.settings")} onHome={() => go("inbox")} />;
  } else if (ownerNeedsContext) {
    content = <OwnerNoContext onOpen={() => go("businesses")} />;
  } else if (view.name === "mail") {
    content = <WorkspaceScreen key={`${scopeKey}:${view.id}`} requestId={view.id} flags={flags} features={config?.features ?? null} onBack={returnFromWorkspace} onTrashed={reconcileTrashedRequest} onOpenRequest={openReq}
      backToEmailQa={entry.returnTo?.scopeKey === scopeKey && entry.returnTo.view === "emailQa"}
      onGotoSettings={isAdmin ? () => go("settings") : undefined}
      onGotoCatalog={isAdmin ? () => navigate({ name: "catalog", returnRequest: view.id }, { scopeKey, returnTo: entry.returnTo }) : undefined} pushToast={pushToast}
      onLocalAssignToMe={suppressAssignmentNotice} />;
  } else if (view.name === "staff") {
    content = isAdmin
      ? <StaffScreen pushToast={pushToast} currentUserId={user.id}
          canCreateUsers={isOwner && Boolean(tenantContext)}
          tab={view.tab}
          onTabChange={(tab) => navigate({ name: "staff", tab }, { replace: true, scopeKey })} />
      : <DeniedScreen reason="admin" area={t("shell.staff")} onHome={() => go("inbox")} />;
  } else if (view.name === "catalog") {
    content = isAdmin && (tenantContext || user.tenantId)
      ? <CatalogScreen tenantId={(tenantContext || user.tenantId)!} canImport={isOwner} pushToast={pushToast}
          onBackToRequest={view.returnRequest ? () => navigate({ name: "mail", id: view.returnRequest! }, { scopeKey, returnTo: entry.returnTo }) : undefined} />
      : <DeniedScreen reason="admin" area={t("shell.catalog")} onHome={() => go("inbox")} />;
  } else if (view.name === "filed") {
    content = <FiledScreen onOpenRequest={openReq} pushToast={pushToast}
      overview={{ href: pathOf({ name: "inbox" }), onOpen: () => go("inbox") }}
      lists={{
        filed: { href: pathOf({ name: "filed" }), count: filedCount, onOpen: () => go("filed") },
        onSelect: (list) => { setRequestedInboxList(list); go("inbox"); },
      }} />;
  } else {
    content = <InboxScreen
      key={`${scopeKey}:${entry.key}:${inboxHomeCount}`}
      onOpen={openReq}
      pushToast={pushToast}
      config={config}
      initialContext={restoredInboxContext}
      onContextChange={rememberInboxContext}
      filed={{ href: pathOf({ name: "filed" }), count: filedCount, onOpen: () => go("filed") }}
      initialList={requestedInboxList ?? undefined}
      onOpenStaff={isAdmin ? () => go("staff") : undefined}
      onOpenSystemStatus={isAdmin ? () => go("settings") : undefined}
    />;
  }

  return (
    <div className="app-root">
      {!signedInPageShown && (
        <div className="center-screen session-loading session-loading--cover" aria-hidden="true">
          <div className="login__mark">
            <Wordmark large />
          </div>
          <p className="subtle" style={{ marginTop: "var(--space-4)" }}>{t("common.loading")}</p>
        </div>
      )}
      <SidebarShell
        nav={nav}
        userId={user.id}
        scopeKey={scopeKey}
        routeKey={entry.key}
        logoHref={pathOf({ name: "inbox" })}
        onLogoClick={() => go("inbox")}
        mainLabel={routeTitle}
        mainRef={mainRef}
        userName={identity.name}
        userSub={identity.sub ?? t(roleLabelKey(role))}
        businessName={user.role === "owner"
          ? tenantContext && user.tenantId === tenantContext ? user.tenantDisplayName : null
          : user.tenantDisplayName}
        businessLogo={businessLogo}
        accountActive={view.name === "account"}
        accountHref={pathOf({ name: "account" })}
        onAccount={() => go("account")}
        onLogout={() => { void logout(); }}
      >
        {tenantReadOnly && (
          <div className="stack-sm" style={{ marginBottom: "var(--space-5)" }}>
            <Banner tone="warning" icon={<Icon name="lock" size={18} />}
              title={t(tenantReadOnly === "archived" ? "shell.tenantArchivedTitle" : "shell.tenantSuspendedTitle")}>
              {t(tenantReadOnly === "archived" ? "shell.tenantArchivedBody" : "shell.tenantSuspendedBody")}
            </Banner>
          </div>
        )}
        {aiOff && (
          <div className="stack-sm" style={{ marginBottom: "var(--space-5)" }}>
            <Banner tone="system" icon={<Icon name="sparkles" size={18} />} title={t("shell.aiBannerTitle")}>
              {t("shell.aiBannerBody")}
            </Banner>
          </div>
        )}
        <React.Fragment key={scopeKey}>{liveReady ? content : null}</React.Fragment>
      </SidebarShell>
    </div>
  );
}

/** Shown to the tenant-less owner on tenant-scoped views: pick a business first (#232). */
function OwnerNoContext({ onOpen }: { onOpen: () => void }) {
  const t = useT();
  return (
    <div className="page">
      <Card>
        <div className="stack-sm" style={{ alignItems: "flex-start" }}>
          <h2 style={{ margin: 0 }}>{t("shell.noBusinessTitle")}</h2>
          <p className="subtle" style={{ margin: 0 }}>{t("shell.noBusinessBody")}</p>
          <Button variant="primary" iconLeft={<Icon name="building" size={16} />} onClick={onOpen}>
            {t("shell.noBusinessCta")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
